import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { MapMarkerNewIcon, PureButton } from '@jsmdg/yoshi';
import { type Location } from '../../../shared/types';
import { useProductDetailScroll } from '../../contexts';
import styles from './ProductLocation.module.scss';

type ProductLocationProps = {
    readonly locations: Location[];
    readonly className?: string;
};

const messages = defineMessages({
    locationLabel: {
        defaultMessage:
            '{locationCount, plural, one {{placeName}} other {an {locationCount, number} Orten}}',
    },
});

const ProductLocation = ({ className, locations }: ProductLocationProps): JSX.Element | null => {
    const intl = useIntl();
    const { scrollToVenues } = useProductDetailScroll();

    // zip code 00000 marks online / nationwide placeholder locations, which are not shown on the map
    const uniqueExternalIds = new Set<string>();
    let placeName = '';

    for (const location of locations) {
        if (location.zipCode === '00000' || uniqueExternalIds.has(location.externalId)) {
            continue;
        }

        uniqueExternalIds.add(location.externalId);

        if (uniqueExternalIds.size === 1) {
            placeName = location.city || location.region || '';
        }
    }

    if (uniqueExternalIds.size === 0) {
        return null;
    }

    const label = intl.formatMessage(messages.locationLabel, {
        locationCount: uniqueExternalIds.size,
        placeName,
    });

    return (
        <PureButton
            className={classNames('d-flex align-items-baseline', className)}
            onClick={scrollToVenues}
        >
            <MapMarkerNewIcon
                ariaHidden
                className={classNames('mr-0-5x align-self-start flex-shrink-0', styles.icon)}
            />
            <span className="text-decoration-underline">{label}</span>
        </PureButton>
    );
};

export { ProductLocation };
